<template>
  <v-container class="modern-detail-view pb-12 position-relative">
    <!-- Top Progress Bar (Global fetching indicator) -->
    <v-progress-linear
      :active="isFetching"
      indeterminate
      color="primary"
      fixed
      top
      height="4"
      style="z-index: 9999;"
    />

    <!-- Fullscreen / Viewport Loading Overlay -->
    <v-fade-transition>
      <div v-if="loading" class="refresh-overlay">
        <v-card class="pa-8 rounded-xl shadow-lg text-center modern-loader-card">
          <v-progress-circular 
            indeterminate 
            color="primary" 
            size="52" 
            width="4" 
            class="mb-4" 
          />
          <div class="text-subtitle-1 font-weight-black primary--text uppercase tracking-widest mb-1">
            {{ loadingText || 'Cargando...' }}
          </div>
          <div class="caption grey--text font-weight-medium">
            Por favor espere un momento
          </div>
        </v-card>
      </div>
    </v-fade-transition>

    <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid, validated }">
      <!-- Global Header -->
      <v-row class="mb-8 align-center px-4 pt-4">
        <v-btn icon @click.stop="$router.go(-1)" class="mr-4 grey lighten-4 shadow-sm" :disabled="loading">
          <v-icon color="primary" small>fas fa-chevron-left</v-icon>
        </v-btn>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
            <v-icon color="white">fas fa-file-invoice</v-icon>
          </v-avatar>
          <div>
            <div class="text-h4 font-weight-black primary--text lh-1">
              {{ viewMode ? `Documento #${id}` : (id > 0 ? `Actualizar Documento #${id}` : ((simpleFacturaEnabled || facturacionClEnabled) ? 'Emitir Documento Electrónico' : 'Registrar Documento')) }}
            </div>
            <div class="mt-1 d-flex align-center flex-wrap">
              <v-chip :color="tipoColor" dark x-small label class="font-weight-black px-2 mr-2">
                <v-icon x-small left>{{ tipoIcon }}</v-icon>
                {{ tipoLabel }}
              </v-chip>
              <v-chip
                v-if="isGuia && related_invoices && related_invoices.length > 0"
                color="success"
                dark
                x-small
                label
                class="font-weight-black px-2 mr-2"
              >
                <v-icon x-small left>fas fa-check-circle</v-icon>
                Facturada ({{ related_invoices.map(i => '#' + (i.folio || i.document_id)).join(', ') }})
              </v-chip>
              <v-chip
                v-if="!isGuia && guiaReferenciada"
                color="info"
                dark
                x-small
                label
                class="font-weight-black px-2 mr-2"
              >
                <v-icon x-small left>fas fa-truck</v-icon>
                Ref. Guía #{{ guiaReferenciada.folio }}
              </v-chip>
              <span class="caption grey--text font-weight-bold uppercase tracking-widest">{{ (simpleFacturaEnabled || facturacionClEnabled) ? (facturacionClEnabled ? 'Facturación.cl · DTE' : 'Simple Factura · DTE') : 'Ventas y Facturación Electrónica' }}</span>
            </div>
          </div>
        </div>
        <v-spacer></v-spacer>
        
        <v-btn 
          v-if="viewMode && isGuia"
          color="success" 
          class="font-weight-black shadow-sm px-6 h-44 mr-3" 
          @click="billCurrentGuia"
          :loading="loading"
        >
          <v-icon left small>fas fa-file-invoice-dollar</v-icon>
          {{ related_invoices && related_invoices.length > 0 ? 'Facturar de Nuevo' : 'Facturar Guía' }}
        </v-btn>
        <v-btn 
          v-if="viewMode"
          color="primary" 
          class="font-weight-black shadow-sm px-8 h-44" 
          @click="$router.push('/sales/invoices')"
          :disabled="loading"
        >
          <v-icon left small>fas fa-list</v-icon>
          Ver en facturas
        </v-btn>
        <v-btn 
          v-else
          :color="invalid && validated ? 'error' : 'success'" 
          class="font-weight-black shadow-sm px-8 h-44" 
          @click="handleSubmit(confirm)" 
          :loading="loading"
        >
          <v-icon left small>{{ invalid && validated ? 'fas fa-exclamation-triangle' : ((simpleFacturaEnabled || facturacionClEnabled) ? 'fas fa-file-arrow-up' : 'fas fa-save') }}</v-icon> 
          {{ invalid && validated ? 'Corregir Errores' : (facturacionClEnabled ? 'Emitir en Facturación.cl' : (simpleFacturaEnabled ? 'Emitir en Simple Factura' : (factronicaActivatedEnabled ? 'Guardar Factronica' : 'Guardar Documento'))) }}
        </v-btn>
      </v-row>
 
      <!-- Banner Informativo: Guía Facturada (viewMode) -->
      <v-alert
        v-if="viewMode && isGuia && related_invoices && related_invoices.length > 0"
        type="success"
        outlined
        dense
        class="mx-4 mb-6 modern-card shadow-sm"
        color="success"
      >
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center">
            <v-icon color="success" class="mr-3">fas fa-check-circle</v-icon>
            <div>
              <div class="subtitle-2 font-weight-black success--text">
                Guía de Despacho Facturada
              </div>
              <div class="caption grey--text text--darken-2">
                Esta guía ya cuenta con {{ related_invoices.length === 1 ? 'una factura electrónica emitida' : related_invoices.length + ' facturas emitidas' }}.
              </div>
            </div>
          </div>
          <div class="d-flex align-center flex-wrap mt-2 mt-sm-0">
            <v-chip
              v-for="inv in related_invoices"
              :key="'inv-' + inv.id"
              small
              color="success"
              outlined
              class="font-weight-black mr-2 my-1"
              @click="$router.push(`/sales/invoices?search=${inv.folio || inv.document_id}`)"
            >
              <v-icon x-small left>fas fa-file-invoice-dollar</v-icon>
              Factura #{{ inv.folio || inv.document_id }}
            </v-chip>
          </div>
        </div>
      </v-alert>

      <!-- Banner Informativo: Factura con Guía Asociada (viewMode) -->
      <v-alert
        v-if="viewMode && !isGuia && guiaReferenciada"
        type="info"
        outlined
        dense
        class="mx-4 mb-6 modern-card shadow-sm"
        color="primary"
      >
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">fas fa-link</v-icon>
            <div>
              <div class="subtitle-2 font-weight-black primary--text">
                Factura Asociada a Guía de Despacho #{{ guiaReferenciada.folio }}
              </div>
              <div class="caption grey--text text--darken-2">
                Este documento fue emitido en base a la Guía DTE 52 Folio #{{ guiaReferenciada.folio }}.
              </div>
            </div>
          </div>
          <v-chip
            small
            color="primary"
            outlined
            class="font-weight-black ml-auto my-1"
            @click="$router.push(`/sales/invoices?tab=guias&search=${guiaReferenciada.folio}`)"
          >
            <v-icon x-small left>fas fa-truck</v-icon>
            Ver Guía de Despacho #{{ guiaReferenciada.folio }}
          </v-chip>
        </div>
      </v-alert>

      <!-- Banner Informativo: Facturando Guía de Despacho (en creación) -->
      <v-alert
        v-if="!viewMode && guiaReferenciada"
        type="info"
        outlined
        dense
        class="mx-4 mb-6 modern-card shadow-sm"
        color="primary"
      >
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">fas fa-truck</v-icon>
            <div>
              <div class="subtitle-2 font-weight-black primary--text">
                Facturando Guía de Despacho #{{ guiaReferenciada.folio }}
              </div>
              <div class="caption grey--text text--darken-2">
                Se han precargado los datos del receptor, productos despachados y la referencia a la Guía DTE 52. Puedes modificar montos o líneas antes de emitir la factura.
              </div>
            </div>
          </div>
          <v-chip small color="primary lighten-5" class="primary--text font-weight-bold ml-auto">
            Ref: Guía 52 · Folio #{{ guiaReferenciada.folio }}
          </v-chip>
        </div>
      </v-alert>

      <v-form @submit.prevent="handleSubmit(confirm)" @keydown.enter.prevent>
        <v-row>
          <!-- Configuration Sidebar -->
          <v-col cols="12" md="3">
            <!-- Basic Info Card -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8" :class="invalid && validated ? 'border-error-light' : ''" :loading="loadingWorkOrder">
              <template slot="progress">
                <v-progress-linear color="primary" height="3" indeterminate></v-progress-linear>
              </template>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Información del Documento</div>
              
              <div class="caption grey--text font-weight-black mb-1 ml-1">Vincular a OT</div>
              <v-text-field
                v-model="work_order"
                placeholder="Nro de Orden de Trabajo..."
                outlined dense
                class="modern-input mb-4"
                :append-icon="work_order_finded ? 'fas fa-check-circle' : 'fas fa-search'"
                :loading="loadingWorkOrder"
                :disabled="viewMode"
                @keydown="searchWorkOrder"
                @change="searchWorkOrder"
                :success="work_order_finded"
                :hint="alertHasInvoices ? 'Ya existe un documento para esta OT' : (simpleFacturaEnabled && !facturacionClEnabled && dte.tipo !== 39 ? 'Requerido para emitir con Simple Factura' : (facturacionClEnabled ? 'Opcional' : ''))"
                :persistent-hint="alertHasInvoices || (simpleFacturaEnabled && !facturacionClEnabled && dte.tipo !== 39)"
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Tipo de Documento</div>
              <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="dte.tipo"
                  :items="computedTipos"
                  item-text="text"
                  item-value="value"
                  outlined dense
                  :hide-details="!errors[0]"
                  class="modern-input mb-4"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <!-- Guía 52: Indicador de Traslado y Tipo de Despacho -->
              <div v-if="dte.tipo === 52" class="mb-4">
                <div class="caption grey--text font-weight-black mb-1 ml-1">Indicador de Traslado</div>
                <v-select
                  v-model="tipo_traslado"
                  :items="traslados"
                  item-text="text"
                  item-value="value"
                  outlined dense hide-details
                  class="modern-input mb-4"
                />

                <div class="caption grey--text font-weight-black mb-1 ml-1">Tipo de Despacho</div>
                <v-select
                  v-model="tipo_despacho"
                  :items="despachos"
                  item-text="text"
                  item-value="value"
                  outlined dense hide-details
                  class="modern-input mb-3"
                />

                <!-- Datos de Transporte para despacho por cuenta del emisor -->
                <div v-if="tipo_despacho === 2 || tipo_despacho === 3" class="mt-3 pt-3 border-top">
                  <div class="caption primary--text font-weight-black text-uppercase tracking-wider mb-2">
                    <v-icon x-small color="primary" class="mr-1">fas fa-truck</v-icon>
                    Datos de Transporte
                  </div>

                  <div class="caption grey--text font-weight-black mb-1 ml-1">RUT Transportista</div>
                  <v-text-field
                    v-model="transporte.rut_trans"
                    placeholder="RUT Empresa (transporte propio) o RUT Transportista"
                    outlined dense
                    class="modern-input mb-2"
                    hint="Si queda vacío, se usa el RUT de su empresa"
                    persistent-hint
                  />

                  <v-row dense>
                    <v-col cols="6">
                      <div class="caption grey--text font-weight-black mb-1 ml-1">Patente</div>
                      <v-text-field
                        v-model="transporte.patente"
                        placeholder="Ej: AB1234"
                        outlined dense hide-details
                        class="modern-input"
                      />
                    </v-col>
                    <v-col cols="6">
                      <div class="caption grey--text font-weight-black mb-1 ml-1">RUT Chofer</div>
                      <v-text-field
                        v-model="transporte.rut_chofer"
                        placeholder="12345678-9"
                        outlined dense hide-details
                        class="modern-input"
                      />
                    </v-col>
                  </v-row>

                  <div class="caption grey--text font-weight-black mb-1 ml-1 mt-2">Nombre Chofer</div>
                  <v-text-field
                    v-model="transporte.nombre_chofer"
                    placeholder="Nombre del chofer"
                    outlined dense hide-details
                    class="modern-input"
                  />
                </div>
              </div>

              <!-- Boleta 39: Medio de Pago -->
              <div v-if="dte.tipo === 39" class="mb-4">
                <div class="caption grey--text font-weight-black mb-1 ml-1">Medio de Pago</div>
                <v-select
                  v-model="payment_method"
                  :items="paymentMethods"
                  item-text="text"
                  item-value="value"
                  outlined dense hide-details
                  class="modern-input"
                />
              </div>

              <v-row dense>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Nro Folio</div>
                  <template v-if="(simpleFacturaEnabled || facturacionClEnabled) && !viewMode && id <= 0">
                    <v-text-field
                      value="Automático"
                      outlined dense readonly
                      class="modern-input"
                      :hint="facturacionClEnabled ? 'Asignado por Facturación.cl' : 'Asignado por Simple Factura'"
                      persistent-hint
                    ></v-text-field>
                  </template>
                  <template v-else>
                    <ValidationProvider name="Folio" rules="required" v-slot="{ errors }">
                      <v-text-field
                        v-model="dte.document_id"
                        outlined dense
                        class="modern-input"
                        :error-messages="errors"
                      ></v-text-field>
                    </ValidationProvider>
                  </template>
                </v-col>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Fecha Emisión</div>
                  <ValidationProvider name="Fecha" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="dte.date_emited"
                      type="date"
                      outlined dense
                      class="modern-input"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card>

            <!-- Referencia de Nota de Crédito (Obligatoria para NC) -->
            <v-card v-if="dte.tipo === 61 || dte.tipo === 60" outlined class="modern-card shadow-sm pa-6 mb-8 border-error-light">
              <div class="caption error--text font-weight-black text-uppercase tracking-widest mb-4">
                <v-icon x-small color="error" class="mr-1">fas fa-file-invoice</v-icon>
                Referencia de Nota de Crédito (Requerida)
              </div>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Documento de Referencia</div>
              <v-select
                v-model="referencia_tipo"
                :items="refTipos"
                item-text="text"
                item-value="value"
                outlined dense hide-details
                class="modern-input mb-4"
              />

              <div class="caption grey--text font-weight-black mb-1 ml-1">Folio Referencia</div>
              <v-text-field
                v-model="referencia_folio"
                placeholder="Ej: 1234"
                outlined dense hide-details
                class="modern-input mb-4"
              />

              <div class="caption grey--text font-weight-black mb-1 ml-1">Fecha Referencia</div>
              <v-text-field
                v-model="referencia_fecha"
                type="date"
                outlined dense hide-details
                class="modern-input mb-4"
              />

              <div class="caption grey--text font-weight-black mb-1 ml-1">Motivo</div>
              <v-select
                v-model="motivo"
                :items="motivos"
                item-text="text"
                item-value="value"
                outlined dense hide-details
                class="modern-input"
              />
            </v-card>

            <!-- Receptor Card -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8" :loading="loadingCustomer">
              <template slot="progress">
                <v-progress-linear color="primary" height="3" indeterminate></v-progress-linear>
              </template>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Datos del Receptor</div>
              
              <div class="caption grey--text font-weight-black mb-1 ml-1">{{$store.getters.getInternational.tax_id_label}}</div>
              <ValidationProvider name="RUT" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="dte.rutRecep"
                  outlined dense
                  class="modern-input mb-4"
                  :loading="loadingCustomer"
                  :error-messages="errors"
                  @keyup="getCustomerDataByRut"
                ></v-text-field>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Razón Social</div>
              <ValidationProvider name="Razon Social" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="dte.rznSocRecep"
                  outlined dense
                  class="modern-input mb-4"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Giro Comercial</div>
              <ValidationProvider name="Giro" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="dte.giroRecep"
                  outlined dense
                  class="modern-input"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>

              <v-alert type="info" dense text v-if="alertCustomerNotInDB" class="mt-4 caption">
                Cliente no registrado en el sistema
              </v-alert>
            </v-card>

            <!-- Additional Detail Card -->
            <v-card outlined class="modern-card shadow-sm pa-6">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Detalles Adicionales</div>
              
              <v-row dense>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Días Venc.</div>
                  <v-text-field
                    v-model="dte.payment"
                    type="number"
                    outlined dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Contacto</div>
                  <v-select
                    :items="contactList"
                    item-text="email"
                    return-object
                    outlined dense
                    placeholder="Elegir..."
                    class="modern-input"
                    @change="selectContact"
                  ></v-select>
                </v-col>
              </v-row>

              <div class="caption grey--text font-weight-black mb-1 ml-1 mt-4">Referencia General</div>
              <v-text-field
                v-model="dte.glosa"
                placeholder="Glosa del documento..."
                outlined dense
                class="modern-input"
              ></v-text-field>

              <v-divider class="my-6"></v-divider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Dirección de Despacho</div>
              <v-select
                :items="addressList"
                :item-text="formatAddressText"
                return-object
                v-model="selectedAddress"
                outlined dense
                placeholder="Elegir dirección..."
                class="modern-input mb-4"
                @change="selectAddress"
              ></v-select>

              <v-text-field
                v-model="dte.dirRecep"
                label="Dirección"
                outlined dense
                class="modern-input mb-4"
                hide-details
              ></v-text-field>
              
              <v-text-field
                v-model="dte.cmnaRecep"
                :label="$store.getters.getInternational.comune_label"
                outlined dense
                class="modern-input"
                hide-details
              ></v-text-field>
            </v-card>
          </v-col>

          <!-- Main Content: Products table -->
          <v-col cols="12" md="9">
            <div class="mb-4 d-flex align-center">
              <v-avatar color="primary" size="32" class="mr-3 shadow-xs">
                <v-icon color="white" x-small>fas fa-boxes</v-icon>
              </v-avatar>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest">
                Detalle de Productos y Servicios
              </div>
              <v-chip v-if="sales_note" color="primary lighten-5" class="primary--text ml-4 font-weight-bold" small label>
                Cotización #{{sales_note}}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn color="success" class="font-weight-black rounded-lg px-6 shadow-sm" @click="()=>dte.dte_detail.push({
                nro_linea:dte.dte_detail.length + 1,
                product:-1,
                sku:'',
                name:'',
                unit:'',
                qty:0,
                price:0
              })">
                <v-icon left small>fas fa-plus</v-icon> Agregar Línea
              </v-btn>
            </div>

            <v-card outlined class="modern-card shadow-sm overflow-hidden mb-8">
              <v-data-table
                :headers="headers"
                :items="dte.dte_detail"
                item-key="nro_linea"
                hide-default-footer
                class="modern-table"
                :items-per-page="-1"
                :loading="loading || loadingWorkOrder"
                loading-text="Cargando productos y servicios..."
              >
                <template v-slot:no-data>
                  <div class="pa-10 text-center fill-width grey--text">
                    <v-icon size="48" color="grey lighten-3" class="mb-4">fas fa-receipt</v-icon>
                    <div class="body-1 font-weight-bold uppercase mb-2">No hay productos en el documento</div>
                    <div class="caption">Agregue líneas de detalle para calcular los totales</div>
                  </div>
                </template>

                <template v-slot:item.sku="{ item }">
                  <v-text-field   
                    v-model='item.sku'
                    outlined dense hide-details
                    class="modern-input-minimal font-weight-black primary--text"
                    :loading="!!item._loading"
                    @keyup="getProductDatabySKU(item)"
                  >
                    <template v-slot:append v-if="item.product<0 && item.sku != ''">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="warning" v-bind="attrs" v-on="on">fas fa-exclamation-triangle</v-icon>
                        </template>
                        <span>Producto no encontrado en inventario</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </template>

                <template v-slot:item.name="{ item }">
                  <v-textarea  
                    v-model='item.name'
                    rows="1"
                    auto-grow
                    outlined dense hide-details
                    class="modern-input-minimal body-2 py-2"
                  />
                </template>

                <template v-slot:item.qty="{ item }">
                  <v-text-field
                    type="number"
                    v-model.number='item.qty'
                    :min="0"
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.unit="{ item }">
                  <v-text-field
                    v-model='item.unit'
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.price="{ item }">
                  <v-text-field
                    type="number"
                    v-model.number='item.price'
                    outlined dense hide-details
                    class="modern-input-minimal text-right"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.total="{ item }">
                  <span class="body-2 font-weight-black primary--text">
                    {{ (item.price * item.qty) | moneda }}
                  </span>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn icon small color="error" class="grey lighten-4" @click.stop="removeArticle(item)">
                    <v-icon x-small>fas fa-trash</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>

            <!-- Totals Card -->
            <v-card outlined class="modern-card shadow-sm pa-8 bg-lightgrey">
              <v-row align="center">
                <v-col cols="12" md="3" class="border-right px-6 text-center">
                  <div class="caption grey--text font-weight-black uppercase mb-1">Subtotal Neto</div>
                  <div class="text-h5 font-weight-black primary--text">{{ neto | moneda }}</div>
                </v-col>
                
                <v-col cols="12" md="4" class="border-right px-6 text-center">
                  <div class="d-flex align-center justify-center mb-1">
                    <span class="caption grey--text font-weight-black uppercase mr-2">{{ $store.getters.getInternational.purchase_tax_label }} (%)</span>
                    <v-text-field 
                      outlined dense hide-details
                      class='modern-input-minimal'
                      style="width: 70px"
                      type='number' 
                      v-model.number="dte.iva" 
                    />
                  </div>
                  <div class="text-h6 font-weight-bold grey--text text--darken-3">
                    {{ (neto * dte.iva * 0.01) | moneda }}
                  </div>
                </v-col>

                <v-col cols="12" md="5" class="px-8 d-flex flex-column align-end">
                  <div class="caption primary--text font-weight-black uppercase tracking-wider mb-1">Total Documento</div>
                  <div class="text-h3 font-weight-black primary--text tracking-tighter">
                    {{ (neto * (1 + dte.iva / 100)) | moneda }}
                  </div>
                  <div class="caption grey--text font-weight-bold mt-2">
                    Vencimiento Estimado: <span class="primary--text">{{ payment_date }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-card>

            <!-- Documentos de Referencia para Facturas, Boletas y Guías (Opcional, Múltiples) -->
            <v-card v-if="dte.tipo !== 61 && dte.tipo !== 60" outlined class="modern-card shadow-sm pa-6 mb-8 mt-6">
              <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
                <div>
                  <div class="caption primary--text font-weight-black text-uppercase tracking-widest">
                    <v-icon x-small color="primary" class="mr-1">fas fa-link</v-icon>
                    Documentos de Referencia (Opcional)
                  </div>
                  <div class="caption grey--text mt-1">
                    Asocie Órdenes de Compra (801), Guías (52), Contratos u otros documentos.
                  </div>
                </div>
                <v-btn
                  v-if="!viewMode"
                  small
                  color="primary"
                  outlined
                  depressed
                  class="rounded-lg font-weight-bold my-1"
                  @click="addReferencia"
                >
                  <v-icon x-small left>fas fa-plus</v-icon>
                  Agregar Referencia
                </v-btn>
              </div>

              <!-- Estado vacío cuando no hay referencias -->
              <div v-if="!referencias || referencias.length === 0" class="text-center py-5 grey lighten-5 rounded-lg border-dashed">
                <v-icon color="grey lighten-1" class="mb-2">fas fa-file-signature</v-icon>
                <div class="caption grey--text text--darken-1 font-weight-bold">
                  {{ viewMode ? 'No registra documentos de referencia' : 'No hay referencias agregadas a este documento' }}
                </div>
                <div class="caption grey--text text--lighten-1 mt-1">
                  {{ viewMode ? 'Este documento no incluye referencias tributarias adicionales.' : 'Si requiere Orden de Compra (OC) o guía de despacho, haz clic en "Agregar Referencia".' }}
                </div>
              </div>

              <!-- Lista dinámica de referencias -->
              <div v-else>
                <div
                  v-for="(ref, index) in referencias"
                  :key="'ref-' + index"
                  class="grey lighten-5 pa-4 rounded-lg mb-3 border-light"
                >
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="caption font-weight-bold grey--text text--darken-2 d-flex align-center">
                      <v-chip x-small color="primary" dark label class="mr-2 font-weight-black">Ref #{{ index + 1 }}</v-chip>
                      <span>{{ getRefLabel(ref.tipo) }}</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-btn
                        v-if="viewMode && (Number(ref.tipo) === 50 || Number(ref.tipo) === 52) && ref.folio"
                        x-small
                        text
                        color="primary"
                        class="font-weight-bold mr-2"
                        @click="$router.push(`/sales/invoices?tab=guias&search=${ref.folio}`)"
                      >
                        <v-icon x-small left>fas fa-truck</v-icon>
                        Ver Guía #{{ ref.folio }}
                      </v-btn>
                      <v-btn v-if="!viewMode" icon x-small color="error" @click="removeReferencia(index)" title="Eliminar referencia">
                        <v-icon x-small>fas fa-trash-alt</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <v-row dense>
                    <v-col cols="12" :sm="Number(dte.tipo) === 39 ? 6 : 6" :md="Number(dte.tipo) === 39 ? 4 : 4">
                      <div class="caption grey--text font-weight-black mb-1 ml-1">Tipo de Documento</div>
                      <v-select
                        v-model="ref.tipo"
                        :items="refDocTipos"
                        item-text="text"
                        item-value="value"
                        outlined dense hide-details
                        :disabled="viewMode"
                        class="modern-input bg-white"
                      />
                    </v-col>
                    <v-col cols="12" :sm="Number(dte.tipo) === 39 ? 6 : 6" :md="Number(dte.tipo) === 39 ? 4 : 3">
                      <div class="caption grey--text font-weight-black mb-1 ml-1">Folio / N° Documento</div>
                      <v-text-field
                        v-model="ref.folio"
                        placeholder="Ej: 1234 o OC-987"
                        outlined dense hide-details
                        :readonly="viewMode"
                        class="modern-input bg-white"
                      />
                    </v-col>
                    <v-col v-if="Number(dte.tipo) !== 39" cols="12" sm="6" md="2">
                      <div class="caption grey--text font-weight-black mb-1 ml-1">Fecha</div>
                      <v-text-field
                        v-model="ref.fecha"
                        type="date"
                        outlined dense hide-details
                        :readonly="viewMode"
                        class="modern-input bg-white"
                      />
                    </v-col>
                    <v-col cols="12" :sm="Number(dte.tipo) === 39 ? 12 : 6" :md="Number(dte.tipo) === 39 ? 4 : 3">
                      <div class="caption grey--text font-weight-black mb-1 ml-1">Razón / Glosa (Opcional)</div>
                      <v-text-field
                        v-model="ref.razon"
                        placeholder="Ej: OC Cliente"
                        outlined dense hide-details
                        :readonly="viewMode"
                        class="modern-input bg-white"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>

    <!-- Progress Dialog -->
    <v-dialog v-model="showProgress" persistent max-width="450">
      <v-card class="modern-card shadow-sm pa-10 text-center"> 
        <div class="text-h5 font-weight-black primary--text mb-6">Procesando Documento</div>
        <v-progress-circular 
          :rotate="-90"
          :size="120"
          :width="15"
          v-model="progress"
          color="primary"
          class="mb-6"
        >
          <span class="text-h6 font-weight-black">{{ progress | number('0.0') }}%</span>
        </v-progress-circular>
        <div class="caption grey--text font-weight-black uppercase tracking-widest">Enviando datos al servidor...</div>
      </v-card>
    </v-dialog>

    <confirm 
      v-model="showConfirm" 
      :message="confirmDialogMessage" 
      @ok="create"
      @cancel="showConfirm=false"
    />
  </v-container>
</template>

<script>
import { createDTE, getDTE, fetchDTE, getWorkOrderforDTE } from '@/api/salesNotes'
import { getCustomer, getCustomerByRUT } from '@/api/customers'
import { getProductbySKU } from '@/api/products'
import { createFactronicaDTE } from '@/api/factronica'
import { createDocument, downloadDocumentPdf, getDocument, fetchDocuments } from '@/api/simplefactura'
import {
  createDocument as createFclDocument,
  downloadDocumentPdf as downloadFclDocumentPdf,
  getDocument as getFclDocument,
  fetchDocuments as fetchFclDocuments
} from '@/api/facturacioncl'
import notifications from '@/utils/notifications'
import { validaRUT } from '@/utils/rut_validator'
import moment from 'moment'

export default {
  name: 'CreateDTE',
  data() {
    return {
      loading: false,
      loadingText: '',
      showConfirm: false,
      rules: {
        rut: value => validaRUT(value) || 'Rut Inválido',
      },
      progress: 0,
      showProgress: false,
      id: -1,
      viewMode: false,
      work_order: '',
      sales_note: '',
      loadingWorkOrder: false,
      work_order_finded: false,
      alertCustomerNotInDB: false,
      alertProductsnotInDB: false,
      alertHasInvoices: false,
      sales_discount: 0,
      timer: null,
      workOrderTimer: null,
      customerTimer: null,
      skuTimer: null,
      selectedAddress: null,
      selectedAddressId: null,
      tipo_traslado: 1,
      tipo_despacho: 1,
      transporte: {
        rut_trans: '',
        patente: '',
        nombre_chofer: '',
        rut_chofer: '',
      },
      payment_method: 'transferencia',
      referencias: [],
      related_invoices: [],
      referenced_guide: null,
      referencia_tipo: 33,
      referencia_folio: '',
      referencia_fecha: moment().format('YYYY-MM-DD'),
      motivo: 1,
      traslados: [
        { value: 1, text: '1 - Operación constituye venta' },
        { value: 2, text: '2 - Ventas por efectuar' },
        { value: 3, text: '3 - Consignaciones' },
        { value: 4, text: '4 - Entrega gratuita' },
        { value: 5, text: '5 - Traslados internos' },
        { value: 6, text: '6 - Otros traslados no venta' },
        { value: 7, text: '7 - Guía de devolución' },
      ],
      despachos: [
        { value: 1, text: '1 - Despacho por cuenta del receptor' },
        { value: 2, text: '2 - Por cuenta del emisor a cliente' },
        { value: 3, text: '3 - Por cuenta del emisor a otras instalaciones' },
      ],
      paymentMethods: [
        { value: 'transferencia', text: 'Transferencia' },
        { value: 'efectivo', text: 'Efectivo' },
        { value: 'tarjeta', text: 'Tarjeta Débito/Crédito' },
        { value: 'cheque', text: 'Cheque' },
        { value: 'otro', text: 'Otro' },
      ],
      refDocTipos: [
        { value: 801, text: '801 - Orden de Compra (OC)' },
        { value: 52, text: '52 - Guía de Despacho' },
        { value: 33, text: '33 - Factura Electrónica' },
        { value: 802, text: '802 - Nota de Pedido' },
        { value: 803, text: '803 - Contrato' },
        { value: 'HES', text: 'HES - Hoja de Entrada Servicios' },
        { value: 39, text: '39 - Boleta Electrónica' },
        { value: 999, text: '999 - Otro Documento' },
      ],
      refTipos: [
        { value: 33, text: 'Factura 33' },
        { value: 39, text: 'Boleta 39' },
        { value: 52, text: 'Guía 52' },
      ],
      motivos: [
        { value: 1, text: '1 - Anula documento' },
        { value: 2, text: '2 - Corrige texto' },
        { value: 3, text: '3 - Corrige montos' },
      ],
      headers: [
        { text: 'Código/SKU', align: 'start', sortable: false, value: 'sku', class: 'table-header', width: '110px' },
        { text: 'Descripción del Producto / Servicio', align: 'start', sortable: false, value: 'name', class: 'table-header' },
        { text: 'Unidad', align: 'center', sortable: false, value: 'unit', class: 'table-header', width: '90px' },
        { text: 'Cant.', align: 'center', sortable: false, value: 'qty', width: '120px', class: 'table-header' },
        { text: 'Precio Unit.', align: 'end', sortable: false, value: 'price', width: '150px', class: 'table-header' },
        { text: 'Total', align: 'end', sortable: false, value: 'total', width: '130px', class: 'table-header' },
        { text: '', align: 'end', sortable: false, value: 'actions', width: '50px', class: 'table-header' },
      ],
      dte: {
        document_id: '',
        tipo: 30,
        date_emited: new Date().toISOString().substr(0, 10),
        customer: '',
        rutRecep: '',
        rznSocRecep: '',
        giroRecep: '',
        contactRecep: '',
        dirRecep: '',
        cmnaRecep: '',
        glosa: '',
        iva: parseInt(this.$store.getters.getCompany.tax),
        payment: 0,
        dte_detail: []
      },
      loadingCustomer: false,
      contactList: [],
      addressList: [],
    }
  },
  methods: {
    formatAddressText(addr) {
      if (!addr) return ''
      const parts = [addr.street, addr.number, addr.comune, addr.city].filter(Boolean)
      const base = parts.join(' ')
      return addr.name ? `${addr.name} (${base})` : base
    },
    removeArticle(item) {
      this.dte.dte_detail = this.dte.dte_detail.filter(i => i.nro_linea != item.nro_linea)
    },
    confirm() {
      if (this.dte.dte_detail.length <= 0) {
        notifications.showError('Deben existir elementos en el documento')
        return
      }
      if (this.simpleFacturaEnabled || this.facturacionClEnabled) {
        const provider = this.facturacionClEnabled ? 'Facturación.cl' : 'Simple Factura'
        if (!this.facturacionClEnabled && Number(this.dte.tipo) !== 39 && !this.work_order) {
          notifications.showError(`Debe indicar una Orden de Trabajo para emitir en ${provider}`)
          return
        }
        if (Number(this.dte.tipo) === 61 && !this.referencia_folio) {
          notifications.showError('La nota de crédito requiere folio de referencia')
          return
        }
        if (this.facturacionClEnabled && !this.dte.customer) {
          notifications.showError('Debe seleccionar un cliente del maestro para emitir en Facturación.cl')
          return
        }
      }
      this.showConfirm = true
    },
    async getCustomerData(customer_id) {
      this.loadingCustomer = true
      try {
        const data = await getCustomer(customer_id)
        this.alertCustomerNotInDB = false
        this.dte.customer = data.id
        this.dte.rutRecep = data.rut
        this.dte.rznSocRecep = data.name
        this.dte.giroRecep = data.giro
        this.dte.payment = data.payment
        this.contactList = data.contacts || []
        if (this.contactList.length > 0) this.selectContact(this.contactList[0])
        this.addressList = data.addresses || []
        if (this.addressList.length > 0) {
          const defaultAddr = this.addressList.find(a => a.default) || this.addressList[0]
          this.selectAddress(defaultAddr)
        }
      } catch (e) {
        notifications.showError('Error cargando datos del cliente')
      } finally {
        this.loadingCustomer = false
      }
    },
    getCustomerDataByRut() {
      if (this.customerTimer) clearTimeout(this.customerTimer)
      if (this.timer) clearTimeout(this.timer)
      this.customerTimer = setTimeout(async () => {
        if (validaRUT(this.dte.rutRecep)) {
          this.loadingCustomer = true
          try {
            const data = await getCustomerByRUT(this.dte.rutRecep)
            this.alertCustomerNotInDB = false
            this.dte.customer = data.id
            this.dte.rznSocRecep = data.name
            this.dte.giroRecep = data.giro
            this.dte.payment = data.payment
            this.contactList = data.contacts || []
            if (this.contactList.length > 0) this.selectContact(this.contactList[0])
            this.addressList = data.addresses || []
            if (this.addressList.length > 0) {
              const defaultAddr = this.addressList.find(a => a.default) || this.addressList[0]
              this.selectAddress(defaultAddr)
            }
          } catch (e) {
            notifications.showInfo('Cliente no encontrado en BBDD')
            this.alertCustomerNotInDB = true
          } finally {
            this.loadingCustomer = false
          }
        }
      }, 1000)
    },
    addReferencia() {
      if (!this.referencias) this.referencias = []
      this.referencias.push({
        tipo: 801,
        folio: '',
        fecha: this.dte.date_emited || moment().format('YYYY-MM-DD'),
        razon: '',
      })
    },
    removeReferencia(index) {
      if (this.referencias && this.referencias.length > index) {
        this.referencias.splice(index, 1)
      }
    },
    getRefLabel(tipo) {
      const found = this.refDocTipos.find(t => t.value == tipo)
      return found ? found.text : `Tipo ${tipo}`
    },
    getProductDatabySKU(item) {
      if (this.skuTimer) clearTimeout(this.skuTimer)
      this.skuTimer = setTimeout(async () => {
        if (!item.sku) return
        this.$set(item, '_loading', true)
        try {
          const data = await getProductbySKU(item.sku)
          item.product = data.id
          item.name = data.name
          item.price = data.price
          item.unit = data.sell_unit_m2 ? 'MTS2' : 'UN'
          item.qty = item.qty || 1
        } catch (e) {
          item.product = -1
        } finally {
          this.$set(item, '_loading', false)
        }
      }, 800)
    },
    selectContact(val) {
      if (!val) return
      const { first_name, last_name } = val
      this.dte.contactRecep = `${first_name || ''} ${last_name || ''}`.trim()
    },
    selectAddress(val) {
      if (!val) return
      const { street, number, comune, id } = val
      this.selectedAddress = val
      this.selectedAddressId = id
      this.dte.dirRecep = `${street || ''} ${number || ''}`.trim()
      this.dte.cmnaRecep = comune || ''
    },
    async fetchWorkOrder(workOrderNumber, isInitial = false) {
      if (!workOrderNumber) return
      if (isInitial) {
        this.loading = true
        this.loadingText = `Cargando Orden de Trabajo #${workOrderNumber}...`
      }
      this.loadingWorkOrder = true
      try {
        const data = await getWorkOrderforDTE(workOrderNumber)
        this.work_order_finded = true
        const { customer, title, products, id, additional_costs, totals, sales_note, has_invoices, purchase_order } = data
        this.alertHasInvoices = has_invoices
        this.sales_note = sales_note
        if (purchase_order && (!this.referencias || this.referencias.length === 0)) {
          this.referencias = [{
            tipo: 801,
            folio: String(purchase_order).trim(),
            fecha: this.dte.date_emited || moment().format('YYYY-MM-DD'),
            razon: `OC OT ${id}`,
          }]
        }
        this.dte.customer = customer.id
        this.dte.rutRecep = customer.rut
        this.dte.rznSocRecep = customer.name
        this.dte.giroRecep = customer.giro
        this.dte.payment = customer.payment
        this.contactList = customer.contacts || []
        if (this.contactList.length > 0) this.selectContact(this.contactList[0])
        this.addressList = customer.addresses || []
        if (this.addressList.length > 0) {
          const defaultAddr = this.addressList.find(a => a.default) || this.addressList[0]
          this.selectAddress(defaultAddr)
        }
        this.dte.work_order = id
        this.dte.glosa = title
        this.sales_discount = totals.discount
        this.dte.dte_detail = []
        
        products.forEach(p => {
          this.dte.dte_detail.push({
            nro_linea: this.dte.dte_detail.length + 1,
            product: p.id,
            sku: p.sku,
            name: p.name,
            unit: p.sell_unit_m2 ? 'MTS2' : 'UN',
            qty: p.sell_unit_m2 ? p.totalMts2 : p.qty,
            price: this.$options.filters.number((this.$options.filters.number(p.price, '0') * (1 - totals.discount * 0.01)), '0'),
          })
        })
        additional_costs.forEach(c => {
          this.dte.dte_detail.push({
            nro_linea: this.dte.dte_detail.length + 1,
            product: -1,
            sku: c.name.toUpperCase().slice(0, 4),
            name: c.name,
            unit: c.cost_unit,
            qty: c.unit === 1 ? totals.mts2 : c.qty,
            price: this.$options.filters.number((this.$options.filters.number(c.sell_price, '0') * (1 - totals.discount * 0.01)), '0'),
          })
        })
      } catch (e) {
        notifications.showInfo('Orden de Trabajo no encontrada')
        this.work_order_finded = false
      } finally {
        this.loadingWorkOrder = false
        if (isInitial) {
          this.loading = false
          this.loadingText = ''
        }
      }
    },
    searchWorkOrder() {
      if (this.workOrderTimer) clearTimeout(this.workOrderTimer)
      if (this.timer) clearTimeout(this.timer)
      this.workOrderTimer = setTimeout(() => {
        this.fetchWorkOrder(this.work_order, false)
      }, 800)
    },
    async create() {
      this.showConfirm = false
      if (this.dte.dte_detail.length <= 0) {
        notifications.showError('Deben existir items en el documento')
        return
      }
      this.loading = true
      this.loadingText = (this.simpleFacturaEnabled || this.facturacionClEnabled)
        ? (this.facturacionClEnabled ? 'Emitiendo documento en Facturación.cl...' : 'Emitiendo documento en Simple Factura...')
        : (this.factronicaActivatedEnabled ? 'Creando documento en Factronica...' : 'Guardando documento...')

      if (this.simpleFacturaEnabled || this.facturacionClEnabled) {
        const payload = {
          workorder_id: this.work_order || this.dte.work_order || null,
          tipo: Number(this.dte.tipo),
          direccion: this.selectedAddressId || (this.addressList[0] && this.addressList[0].id) || null,
          customer_id: this.dte.customer || null,
          neto: this.neto,
          iva: this.dte.iva,
          observacion: this.dte.glosa || (this.work_order ? `OT ${this.work_order}` : ''),
          fecha_pago: moment(this.dte.date_emited).add(this.dte.payment || 0, 'days').format('YYYY-MM-DD'),
          payment_days: Number(this.dte.payment || 0),
          fecha: this.dte.date_emited || moment().format('YYYY-MM-DD'),
          tipo_traslado: this.tipo_traslado,
          ind_traslado: this.tipo_traslado,
          tipo_despacho: this.tipo_despacho,
          detalle: this.dte.dte_detail.map(p => ({
            sku: p.sku || '',
            name: p.name || 'Item',
            qty: Number(p.qty || 0),
            price: Math.round(Number(p.price || 0)),
            unit: p.unit || 'UN',
          })),
        }
        if (Number(this.dte.tipo) === 61) {
          payload.referencia_tipo = this.referencia_tipo
          payload.referencia_folio = this.referencia_folio
          payload.referencia_fecha = this.referencia_fecha
          payload.motivo = this.motivo
          payload.razon_ref = this.ncMotivoLabel || payload.observacion
          payload.referencias = [{
            tipo: this.referencia_tipo,
            folio: this.referencia_folio,
            fecha: this.referencia_fecha,
            motivo: this.motivo,
            razon: this.ncMotivoLabel || payload.observacion,
          }]
        } else if (this.referencias && this.referencias.length > 0) {
          const validRefs = this.referencias.filter(r => r && r.folio && String(r.folio).trim())
          if (validRefs.length > 0) {
            const isBoleta = Number(this.dte.tipo) === 39
            payload.referencias = validRefs.map(r => ({
              tipo: r.tipo,
              folio: String(r.folio).trim(),
              fecha: isBoleta ? undefined : (r.fecha || this.dte.date_emited || moment().format('YYYY-MM-DD')),
              razon: r.razon ? String(r.razon).trim() : '',
            }))
            payload.referencia_tipo = validRefs[0].tipo
            payload.referencia_folio = String(validRefs[0].folio).trim()
            if (!isBoleta) {
              payload.referencia_fecha = validRefs[0].fecha || this.dte.date_emited || moment().format('YYYY-MM-DD')
            }
            payload.razon_ref = validRefs[0].razon ? String(validRefs[0].razon).trim() : ''
          }
        }
        if (Number(this.dte.tipo) === 52) {
          payload.rut_trans = this.transporte.rut_trans
          payload.patente = this.transporte.patente
          payload.nombre_chofer = this.transporte.nombre_chofer
          payload.rut_chofer = this.transporte.rut_chofer
          payload.transporte = this.transporte
        }
        if (Number(this.dte.tipo) === 39) {
          payload.payment_method = this.payment_method
        }

        try {
          const providerName = this.facturacionClEnabled ? 'Facturación.cl' : 'Simple Factura'
          const res = this.facturacionClEnabled ? await createFclDocument(payload) : await createDocument(payload)
          if (res.status === 'Ok' || res.folio) {
            notifications.showSuccess(`Documento emitido en ${providerName}. Folio: ${res.folio}`)
            if (res.id) {
              try {
                if (this.facturacionClEnabled) {
                  await downloadFclDocumentPdf(res.id)
                } else {
                  await downloadDocumentPdf(res.id)
                }
              } catch (pdfErr) {
                console.warn('No se pudo abrir el PDF', pdfErr)
              }
            }
            this.$router.push('/sales/invoices')
          } else {
            notifications.showError(res.error || `Error al emitir documento en ${providerName}`)
          }
        } catch (e) {
          const providerName = this.facturacionClEnabled ? 'Facturación.cl' : 'Simple Factura'
          const message = typeof e === 'string' ? e : (e && e.error) || (e && e.response && e.response.data && e.response.data.error) || `Error al emitir documento en ${providerName}`
          notifications.showError(message)
        } finally {
          this.loading = false
          this.loadingText = ''
        }
        return
      }

      // Flujo tradicional (Factronica o registro DTE local)
      const payload = JSON.parse(JSON.stringify(this.dte))
      if (!payload.customer) delete payload.customer
      
      // Ajuste de precios para NC
      if (payload.tipo == 60) {
        payload.dte_detail.forEach(d => { d.price = -1 * d.price })
      }

      try {
        let created = null
        if (this.factronicaActivatedEnabled) {
          created = await createFactronicaDTE(payload)
          notifications.showSuccess('Documento creado en Factronica')
        } else {
          created = await createDTE(payload)
          notifications.showSuccess('Documento registrado con éxito')
        }
        const newId = created && created.id
        if (newId) {
          this.$router.replace({ name: 'CreateDTE', params: { id: newId, tipo: created.tipo || payload.tipo } }).catch(() => {})
          await this.loadExisting(newId)
        } else {
          this.$router.push('/sales/invoices')
        }
      } catch (e) {
        notifications.showError('Error al procesar documento: ' + e)
      } finally {
        this.loading = false
        this.loadingText = ''
      }
    },
    applyDTE(data) {
      const workOrderId = data.work_order && data.work_order.id ? data.work_order.id : data.work_order
      this.id = data.id
      this.work_order = workOrderId || ''
      this.work_order_finded = !!workOrderId
      this.sales_note = data.sales_note || ''
      this.dte.document_id = data.document_id
      this.dte.tipo = data.tipo
      this.dte.date_emited = data.date_emited
      this.dte.customer = data.customer && data.customer.id ? data.customer.id : data.customer
      this.dte.rutRecep = data.rutRecep
      this.dte.rznSocRecep = data.rznSocRecep
      this.dte.giroRecep = data.giroRecep
      this.dte.contactRecep = data.contactRecep
      this.dte.dirRecep = data.dirRecep
      this.dte.cmnaRecep = data.cmnaRecep
      this.dte.glosa = data.glosa
      this.dte.iva = data.iva
      this.dte.payment = data.payment
      this.dte.work_order = workOrderId
      this.dte.dte_detail = (data.details || data.dte_detail || []).map((row, idx) => ({
        nro_linea: row.nro_linea || idx + 1,
        product: row.product && row.product.id ? row.product.id : (row.product || -1),
        sku: row.sku,
        name: row.name,
        unit: row.unit,
        qty: row.qty,
        price: row.price,
      }))
      this.related_invoices = data.related_invoices || []
      this.referenced_guide = data.referenced_guide || null
      this.referencia_folio = data.referencia_folio ? String(data.referencia_folio).trim() : ''
      this.referencia_tipo = data.referencia_tipo || 52
      
      if (data.referencias && Array.isArray(data.referencias) && data.referencias.length > 0) {
        this.referencias = data.referencias
      } else if (this.referencia_folio) {
        const isGuiaRef = [50, 52].includes(Number(this.referencia_tipo))
        this.referencias = [{
          tipo: Number(this.referencia_tipo),
          folio: this.referencia_folio,
          fecha: data.date_emited ? String(data.date_emited).slice(0, 10) : moment().format('YYYY-MM-DD'),
          razon: isGuiaRef ? `Guía de Despacho N° ${this.referencia_folio}` : (data.referencia_tipo_display || `Referencia ${this.referencia_folio}`),
        }]
      } else {
        this.referencias = []
      }
    },
    async loadExisting(id) {
      this.loading = true
      this.loadingText = `Cargando documento #${id}...`
      try {
        const data = await getDTE(id)
        this.applyDTE(data)
        this.viewMode = true
      } catch (e) {
        notifications.showError('No se pudo cargar el documento generado')
      } finally {
        this.loading = false
        this.loadingText = ''
      }
    },
    mapSfDocToGuiaData(sfDoc) {
      if (!sfDoc) return null
      let details = (sfDoc.detalle || []).map((row, idx) => ({
        nro_linea: row.nro_linea || idx + 1,
        product: row.product && row.product.id ? row.product.id : (row.product || -1),
        sku: row.sku || '',
        name: row.name || '',
        unit: row.unit || 'UN',
        qty: Number(row.qty || 0),
        price: Number(row.price || 0),
      }))
      return {
        work_order: sfDoc.workorder,
        sales_note: sfDoc.sales_note,
        document_id: sfDoc.folio,
        folio: sfDoc.folio,
        date_emited: sfDoc.created_at ? String(sfDoc.created_at).slice(0, 10) : moment().format('YYYY-MM-DD'),
        customer: sfDoc.customer,
        rutRecep: sfDoc.customer_rut,
        rznSocRecep: sfDoc.customer_name,
        giroRecep: sfDoc.customer_giro || '',
        contactRecep: sfDoc.customer_contact || '',
        dirRecep: sfDoc.customer_address || '',
        cmnaRecep: sfDoc.customer_commune || '',
        payment: sfDoc.payment || 30,
        iva: sfDoc.neto ? Math.round((Number(sfDoc.iva_amount || 0) / Number(sfDoc.neto)) * 100) : 19,
        details,
        dte: sfDoc.dte,
      }
    },
    async fallbackPopulateGuia(ot, folio) {
      this.id = -1
      this.viewMode = false
      this.dte.tipo = (this.simpleFacturaEnabled || this.facturacionClEnabled) ? 33 : 30
      if (folio) {
        this.referencia_folio = String(folio).trim()
        this.referencia_tipo = 52
        this.referencias = [{
          tipo: 52,
          folio: String(folio).trim(),
          fecha: moment().format('YYYY-MM-DD'),
          razon: `Guía de Despacho N° ${folio}`,
        }]
      }
      if (ot) {
        this.work_order = ot
        await this.fetchWorkOrder(ot, false)
      }
      notifications.showWarning('No se encontró el detalle completo de la guía en el servidor, pero se precargó la referencia y OT')
    },
    async populateFromGuia(data) {
      this.id = -1
      this.viewMode = false
      this.dte.tipo = (this.simpleFacturaEnabled || this.facturacionClEnabled) ? 33 : 30

      const workOrderId = data.work_order && data.work_order.id ? data.work_order.id : data.work_order
      this.work_order = workOrderId || ''
      this.work_order_finded = !!workOrderId
      this.dte.work_order = workOrderId || ''
      this.sales_note = data.sales_note || ''

      this.dte.customer = data.customer && data.customer.id ? data.customer.id : (data.customer || null)
      this.dte.rutRecep = data.rutRecep || ''
      this.dte.rznSocRecep = data.rznSocRecep || ''
      this.dte.giroRecep = data.giroRecep || ''
      this.dte.contactRecep = data.contactRecep || ''
      this.dte.dirRecep = data.dirRecep || ''
      this.dte.cmnaRecep = data.cmnaRecep || ''
      this.dte.payment = data.payment || 30
      this.dte.iva = data.iva || 19

      const rawDetails = data.details || data.dte_detail || []
      this.dte.dte_detail = rawDetails.map((row, idx) => ({
        nro_linea: row.nro_linea || idx + 1,
        product: row.product && row.product.id ? row.product.id : (row.product || -1),
        sku: row.sku || '',
        name: row.name || '',
        unit: row.unit || 'UN',
        qty: Number(row.qty || 0),
        price: Number(row.price || 0),
      }))

      // Si no tiene líneas pero tiene OT, buscar productos desde la OT
      if ((!this.dte.dte_detail || this.dte.dte_detail.length === 0) && workOrderId) {
        try {
          const woData = await getWorkOrderforDTE(workOrderId)
          if (woData && woData.products && woData.products.length > 0) {
            this.dte.dte_detail = woData.products.map((p, idx) => ({
              nro_linea: idx + 1,
              product: p.product || -1,
              sku: p.sku || '',
              name: p.name || '',
              unit: p.unit || 'UN',
              qty: Number(p.qty || 0),
              price: Number(p.price || 0),
            }))
          }
        } catch (_) {}
      }

      const folioGuia = String(data.document_id || data.folio || this.$route.query.ref_folio || '').trim()
      const fechaGuia = data.date_emited ? String(data.date_emited).slice(0, 10) : moment().format('YYYY-MM-DD')

      this.referencia_folio = folioGuia
      this.referencia_tipo = 52
      this.referencias = [{
        tipo: 52,
        folio: folioGuia,
        fecha: fechaGuia,
        razon: folioGuia ? `Guía de Despacho N° ${folioGuia}` : 'Guía de Despacho',
      }]

      // Cargar direcciones y contactos del cliente
      if (this.dte.customer) {
        try {
          const cust = await getCustomer(this.dte.customer)
          if (cust) {
            this.addressList = cust.addresses || []
            this.contactList = cust.contacts || []
            if (!this.dte.dirRecep && this.addressList.length > 0) {
              const defaultAddr = this.addressList.find(a => a.default) || this.addressList[0]
              this.selectAddress(defaultAddr)
            }
            if (!this.dte.contactRecep && this.contactList.length > 0) {
              this.selectContact(this.contactList[0])
            }
          }
        } catch (_) {}
      } else if (this.dte.rutRecep) {
        this.getCustomerDataByRut()
      }

      notifications.showSuccess(`Datos cargados desde Guía de Despacho ${folioGuia ? '#' + folioGuia : ''}`)
    },
    async loadFromGuia() {
      this.loading = true
      this.loadingText = 'Cargando datos de la guía de despacho...'
      const query = this.$route.query || {}
      const dteId = query.from_dte_id || (Number(query.from_guia_id) > 0 && !query.from_sf_id ? query.from_guia_id : null)
      const sfId = query.from_sf_id || (String(query.from_guia_id || '').startsWith('sf-') ? String(query.from_guia_id).replace('sf-', '') : null)
      const folio = String(query.ref_folio || '').trim()
      const ot = query.ot

      let data = null

      // 1. Intentar cargar DTE local si hay ID de DTE
      if (dteId) {
        try {
          const localDte = await getDTE(dteId)
          if (localDte && (localDte.id || localDte.document_id)) {
            data = localDte
          }
        } catch (err) {
          console.warn('No se pudo cargar DTE por ID:', dteId, err)
        }
      }

      // 2. Intentar cargar documento SimpleFactura si hay sfId
      if (!data && sfId) {
        try {
          const sfDoc = await getDocument(sfId)
          if (sfDoc && (sfDoc.id || sfDoc.folio)) {
            data = this.mapSfDocToGuiaData(sfDoc)
            if (sfDoc.dte) {
              try {
                const linkedLocal = await getDTE(sfDoc.dte)
                if (linkedLocal) {
                  if (linkedLocal.customer) data.customer = linkedLocal.customer
                  if (linkedLocal.dirRecep) data.dirRecep = linkedLocal.dirRecep
                  if (linkedLocal.cmnaRecep) data.cmnaRecep = linkedLocal.cmnaRecep
                  if (linkedLocal.giroRecep) data.giroRecep = linkedLocal.giroRecep
                  if (linkedLocal.contactRecep) data.contactRecep = linkedLocal.contactRecep
                  if (linkedLocal.details && linkedLocal.details.length) {
                    data.details = linkedLocal.details
                  }
                }
              } catch (_) {}
            }
          }
        } catch (err) {
          console.warn('No se pudo cargar SimpleFactura por ID:', sfId, err)
        }
      }

      // 2.b. Intentar cargar documento FacturacionCl si hay fclId
      const fclId = this.$route.query.from_fcl_id
      if (!data && fclId) {
        try {
          const fclDoc = await getFclDocument(fclId)
          if (fclDoc && (fclDoc.id || fclDoc.folio)) {
            data = this.mapSfDocToGuiaData(fclDoc)
            if (fclDoc.dte) {
              try {
                const linkedLocal = await getDTE(fclDoc.dte)
                if (linkedLocal) {
                  if (linkedLocal.customer) data.customer = linkedLocal.customer
                  if (linkedLocal.dirRecep) data.dirRecep = linkedLocal.dirRecep
                  if (linkedLocal.cmnaRecep) data.cmnaRecep = linkedLocal.cmnaRecep
                  if (linkedLocal.giroRecep) data.giroRecep = linkedLocal.giroRecep
                  if (linkedLocal.contactRecep) data.contactRecep = linkedLocal.contactRecep
                  if (linkedLocal.details && linkedLocal.details.length) {
                    data.details = linkedLocal.details
                  }
                }
              } catch (_) {}
            }
          }
        } catch (err) {
          console.warn('No se pudo cargar FacturacionCl por ID:', fclId, err)
        }
      }

      // 3. Si aún no se encuentra y hay folio, buscar en DTE local por folio
      if (!data && folio) {
        try {
          const res = await fetchDTE(`search=${encodeURIComponent(folio)}&tipo=guias`)
          const results = res.data?.results || res.data || res.results || res || []
          const match = Array.isArray(results) ? results.find(d => String(d.document_id || d.folio) === folio) : null
          if (match) {
            data = match.id ? await getDTE(match.id) : match
          }
        } catch (err) {
          console.warn('No se pudo buscar DTE por folio:', folio, err)
        }
      }

      // 4. Si aún no se encuentra y hay folio, buscar en SimpleFactura o FacturacionCl por folio
      if (!data && folio && this.simpleFacturaEnabled) {
        try {
          const res = await fetchDocuments(`search=${encodeURIComponent(folio)}&tipo=52`)
          const results = res.data?.results || res.data || res.results || res || []
          const match = Array.isArray(results) ? results.find(d => String(d.folio || d.document_id) === folio) : null
          if (match) {
            const sfFull = match.id ? await getDocument(match.id) : match
            data = this.mapSfDocToGuiaData(sfFull)
          }
        } catch (err) {
          console.warn('No se pudo buscar SimpleFactura por folio:', folio, err)
        }
      }
      if (!data && folio && this.facturacionClEnabled) {
        try {
          const res = await fetchFclDocuments(`search=${encodeURIComponent(folio)}&tipo=52`)
          const results = res.data?.results || res.data || res.results || res || []
          const match = Array.isArray(results) ? results.find(d => String(d.folio || d.document_id) === folio) : null
          if (match) {
            const fclFull = match.id ? await getFclDocument(match.id) : match
            data = this.mapSfDocToGuiaData(fclFull)
          }
        } catch (err) {
          console.warn('No se pudo buscar FacturacionCl por folio:', folio, err)
        }
      }

      try {
        if (data) {
          await this.populateFromGuia(data)
        } else {
          await this.fallbackPopulateGuia(ot, folio)
        }
      } catch (err) {
        console.error('Error procesando datos de guía:', err)
        await this.fallbackPopulateGuia(ot, folio)
      } finally {
        this.loading = false
        this.loadingText = ''
      }
    },
    billCurrentGuia() {
      this.loading = true
      this.loadingText = 'Preparando facturación de la guía...'
      const folioGuia = String(this.dte.document_id || '').trim()
      this.$router.push({
        name: 'CreateDTE',
        params: { id: -1, tipo: (this.simpleFacturaEnabled || this.facturacionClEnabled) ? 33 : 30 },
        query: {
          from_dte_id: this.id > 0 ? this.id : undefined,
          from_guia_id: this.id > 0 ? this.id : undefined,
          ref_folio: folioGuia,
          ref_tipo: 52,
          ot: this.work_order || this.dte.work_order || undefined,
        }
      }).catch(() => {
        this.loading = false
        this.loadingText = ''
      })
    },
    async initFromRoute() {
      if (this.$route.params.tipo) {
        this.dte.tipo = Number(this.$route.params.tipo) || this.dte.tipo
      } else if (this.simpleFacturaEnabled || this.facturacionClEnabled) {
        this.dte.tipo = 33
      }
      if ((this.simpleFacturaEnabled || this.facturacionClEnabled) && Number(this.dte.tipo) === 30) {
        this.dte.tipo = 33
      }
      if (this.$route.query.from_dte_id || this.$route.query.from_sf_id || this.$route.query.from_guia_id || Number(this.$route.query.ref_tipo) === 52) {
        await this.loadFromGuia()
      } else if (this.$route.params.id > 0) {
        await this.loadExisting(this.$route.params.id)
      } else if (this.$route.query.ot) {
        this.work_order = this.$route.query.ot
        await this.fetchWorkOrder(this.work_order, true)
      }
      if (this.$route.query.ref_folio && (!this.referencias || this.referencias.length === 0)) {
        this.referencia_folio = String(this.$route.query.ref_folio).trim()
        if (this.$route.query.ref_tipo) {
          this.referencia_tipo = Number(this.$route.query.ref_tipo) || this.referencia_tipo
        }
        if (Number(this.dte.tipo) !== 61) {
          const refTipoNum = Number(this.$route.query.ref_tipo) || 801
          this.referencias = [{
            tipo: refTipoNum,
            folio: String(this.$route.query.ref_folio).trim(),
            fecha: moment().format('YYYY-MM-DD'),
            razon: refTipoNum === 52 ? `Guía de Despacho N° ${this.$route.query.ref_folio}` : '',
          }]
        }
      }
    }
  },
  computed: {
    isFetching() {
      return this.loading || this.loadingWorkOrder || this.loadingCustomer
    },
    isGuia() {
      const t = Number(this.dte.tipo)
      return t === 50 || t === 52
    },
    guiaReferenciada() {
      if (this.isGuia) return null
      if (this.referenced_guide) return this.referenced_guide
      if (!this.referencias || this.referencias.length === 0) {
        if (this.referencia_folio && [50, 52].includes(Number(this.referencia_tipo))) {
          return { folio: this.referencia_folio, tipo: this.referencia_tipo }
        }
        return null
      }
      const found = this.referencias.find(r => r && (Number(r.tipo) === 52 || Number(r.tipo) === 50) && r.folio)
      if (found) return found
      if (this.referencia_folio && [50, 52].includes(Number(this.referencia_tipo))) {
        return { folio: this.referencia_folio, tipo: this.referencia_tipo }
      }
      return null
    },
    neto() {
      return this.dte.dte_detail.reduce((acc, val) => acc + parseFloat(val.qty || 0) * parseFloat(val.price || 0), 0)
    },
    payment_date() {
      return moment(this.dte.date_emited).add(this.dte.payment, 'days').format('DD/MM/YYYY')
    },
    factronicaActivatedEnabled() {
      return this.$store.getters.getCompany.factronica_token ? true : false
    },
    simpleFacturaEnabled() {
      return !!this.$store.getters.getCompany?.simplefactura_integration_enabled
    },
    facturacionClEnabled() {
      return !!this.$store.getters.getCompany?.facturacioncl_integration_enabled
    },
    computedTipos() {
      if (this.simpleFacturaEnabled || this.facturacionClEnabled) {
        return [
          { value: 33, text: 'Factura Electrónica (33)' },
          { value: 52, text: 'Guía de Despacho (52)' },
          { value: 61, text: 'Nota de Crédito (61)' },
          { value: 39, text: 'Boleta Electrónica (39)' },
        ]
      }
      return [
        { value: 30, text: 'Factura' },
        { value: 60, text: 'Nota de Crédito' },
      ]
    },
    tipoLabel() {
      const found = this.computedTipos.find(t => t.value === Number(this.dte.tipo))
      if (found) return found.text
      if (Number(this.dte.tipo) === 30 || Number(this.dte.tipo) === 33) return 'Factura'
      if (Number(this.dte.tipo) === 60 || Number(this.dte.tipo) === 61) return 'Nota de Crédito'
      if (Number(this.dte.tipo) === 52) return 'Guía de Despacho'
      if (Number(this.dte.tipo) === 39) return 'Boleta Electrónica'
      return 'Documento'
    },
    tipoColor() {
      const t = Number(this.dte.tipo)
      if (t === 30 || t === 33) return 'primary'
      if (t === 52) return 'info'
      if (t === 60 || t === 61) return 'error'
      if (t === 39) return 'success'
      return 'secondary'
    },
    tipoIcon() {
      const t = Number(this.dte.tipo)
      if (t === 52) return 'fas fa-truck'
      if (t === 60 || t === 61) return 'fas fa-file-invoice-dollar'
      if (t === 39) return 'fas fa-receipt'
      return 'fas fa-file-invoice'
    },
    ncMotivoLabel() {
      const found = this.motivos.find(item => item.value === this.motivo)
      return found ? found.text : 'Nota de crédito'
    },
    confirmDialogMessage() {
      if (this.simpleFacturaEnabled || this.facturacionClEnabled) {
        const provider = this.facturacionClEnabled ? 'Facturación.cl' : 'Simple Factura'
        const otText = this.dte.work_order ? `vinculada a la OT #${this.dte.work_order}` : ''
        return `Se emitirá una ${this.tipoLabel} en ${provider} ${otText}. ¿Confirmar emisión?`
      }
      return this.dte.work_order ? `Se vinculará a la OT #${this.dte.work_order}. ¿Confirmar registro?` : 'Se creará el registro sin vínculo a OT. ¿Confirmar?'
    }
  },
  mounted() {
    this.initFromRoute()
  },
  watch: {
    '$route.fullPath'() {
      this.initFromRoute()
    }
  }
}
</script>

<style scoped>
.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; transition: all 0.3s ease; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-right { border-right: 1px solid rgba(0,0,0,0.05) !important; }
.border-error-light { border: 1px solid rgba(var(--v-error-base), 0.3) !important; }
.border-dashed { border: 1px dashed #cfd8dc !important; }
.border-light { border: 1px solid rgba(0,0,0,0.06) !important; }
.bg-white { background-color: white !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.5px; }
.h-44 { height: 44px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-input-minimal >>> .v-input__control fieldset { border-radius: 6px !important; border-color: #cbd5e1 !important; }
.modern-input-minimal >>> input, .modern-input-minimal >>> textarea { font-size: 0.85rem !important; padding: 4px 6px !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 52px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 64px !important; border-bottom: 1px solid #f0f3f5 !important; }

.v-btn.shadow-sm { box-shadow: 0 4px 10px rgba(0,0,0,0.1) !important; }

/* Loader and Overlay */
.refresh-overlay { 
  position: fixed; 
  top: 0; 
  left: 0; 
  right: 0; 
  bottom: 0; 
  background: rgba(255, 255, 255, 0.78); 
  backdrop-filter: blur(4px); 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  z-index: 999; 
}

.modern-loader-card {
  min-width: 320px;
  background: white !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.15) !important;
}
</style>